import { useId, useMemo } from "react"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, LabelList } from "recharts"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "./ui/chart"
import {
  positionChartConfig,
  formatDate,
  dateLabelFormatter,
  ChartGradient,
  GlowFilter,
  ChartDot,
  ActiveDot,
} from "../lib/chart-utils.jsx"

const DEFAULT_MAX_DAYS = 15

export function PositionTrendChart({ positions, maxDays = DEFAULT_MAX_DAYS }) {
  const uid = useId().replace(/:/g, "")
  const gradientId = `posGrad-${uid}`
  const glowId = `posGlow-${uid}`

  const { chartData, yDomain, yBase } = useMemo(() => {
    if (!positions || positions.length === 0) return { chartData: [], yDomain: [0, 10], yBase: 10 }
    const displayed = maxDays ? positions.slice(-maxDays) : positions
    let minPos = Infinity
    let maxPos = -Infinity
    const data = displayed.map((item) => {
      if (item.position != null) {
        if (item.position < minPos) minPos = item.position
        if (item.position > maxPos) maxPos = item.position
      }
      return {
        date: item.date,
        dateLabel: formatDate(item.date),
        position: item.position,
      }
    })
    const domain = minPos === Infinity
      ? [0, 10]
      : [maxPos + 1, Math.max(0.5, minPos - 1)]
    const base = minPos === Infinity ? 10 : maxPos + 1
    return { chartData: data, yDomain: domain, yBase: base }
  }, [positions, maxDays])

  if (!positions?.length) {
    return null
  }

  return (
    <ChartContainer config={positionChartConfig} className="h-[260px] w-full">
      <AreaChart
        data={chartData}
        margin={{ top: 32, right: 20, left: 0, bottom: 4 }}
        accessibilityLayer
      >
        <defs>
          <ChartGradient id={gradientId} />
          <GlowFilter id={glowId} />
        </defs>
        <CartesianGrid
          vertical={false}
          strokeDasharray="3 3"
          stroke="hsl(220, 15%, 91%)"
        />
        <XAxis
          dataKey="dateLabel"
          tickLine={false}
          axisLine={false}
          tickMargin={12}
          tick={{ fontSize: 12, fill: "#9ca3af" }}
        />
        <YAxis
          reversed
          domain={yDomain}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          allowDecimals={false}
          width={32}
          tick={{ fontSize: 12, fill: "#9ca3af" }}
        />
        <ChartTooltip
          cursor={{ stroke: "var(--color-position)", strokeWidth: 1, strokeDasharray: "4 4" }}
          content={
            <ChartTooltipContent
              className="backdrop-blur-md bg-white/90 border-gray-200/60 shadow-2xl rounded-xl"
              formatter={(value) => (
                <div className="flex items-center gap-2.5 py-0.5">
                  <div
                    className="h-3 w-3 shrink-0 rounded-full shadow-sm"
                    style={{ backgroundColor: "var(--color-position)" }}
                  />
                  <span className="text-gray-500 text-[13px]">Position</span>
                  <span className="font-mono font-semibold tabular-nums text-gray-900 text-[13px] ml-auto">
                    {value % 1 === 0 ? value : value.toFixed(1)}
                  </span>
                </div>
              )}
              hideLabel={false}
              labelClassName="text-gray-700 text-[13px] pb-1 border-b border-gray-100 mb-1"
              labelFormatter={dateLabelFormatter}
            />
          }
        />
        <Area
          type="monotone"
          dataKey="position"
          stroke="var(--color-position)"
          fill={`url(#${gradientId})`}
          baseValue={yBase}
          strokeWidth={2.5}
          filter={`url(#${glowId})`}
          dot={ChartDot}
          activeDot={ActiveDot}
          animationDuration={800}
          animationEasing="ease-out"
        >
          <LabelList
            dataKey="position"
            position="top"
            offset={12}
            formatter={(value) => (value % 1 === 0 ? value : value.toFixed(1))}
            className="fill-blue-600 font-semibold text-[11px]"
          />
        </Area>
      </AreaChart>
    </ChartContainer>
  )
}
